/**
 * Redux reset actions aligned with legacy `pages/_components/menu/Menu.jsx` (`MenuItem` / `MenuFormItem` `onSelect`)
 * before navigating from v3 rail, flyout submenu, or mobile bottom-nav shortcuts.
 */

import { actions as agendaActions } from "reducers/frequentDestination";
import { actions as checksActions } from "reducers/checks";
import { actions as cedipActions } from "reducers/cedip";
import { actions as debinActions } from "reducers/debin";
import { actions as financingActions } from "reducers/financing";
import { prepareFundcorpOperationsEntry } from "util/fundcorp";
import { actions as globalActions } from "reducers/types/global";
import { actions as paymentsAFIPActions } from "reducers/paymentsAFIP";
import { actions as servicePaymentsActions } from "reducers/servicePayments";
import { isServicePaymentsModuleContinuationPath } from "util/servicePaymentsRoutes";
import { actions as thirdPaymentsActions } from "reducers/thirdPayment";
import { actions as transactionsActions } from "reducers/transactions";
import { actions as transferActions } from "reducers/transfer";
import { isTransferHistoricPath } from "util/v3UxConfig";

/** Destinos del menú v3/legacy que no son flujo transfer; al ir ahí desde `/transfer/summary` hay que limpiar Redux. */
const TRANSFER_SUMMARY_CLEAR_EXACT_PATHS = ["/v3/dashboard", "/desktop", "/v2/accounts", "/v3/accounts"];
const TRANSFER_SUMMARY_CLEAR_PATH_PREFIXES = ["/v2/accounts/", "/v3/accounts/", "/accounts/"];

/**
 * @param {string} path
 * @returns {boolean}
 */
function navigatesAwayFromTransferSummaryToOtherSection(path) {
    if (TRANSFER_SUMMARY_CLEAR_EXACT_PATHS.includes(path)) {
        return true;
    }
    return TRANSFER_SUMMARY_CLEAR_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
}

/**
 * @param {import("redux").Dispatch} dispatch
 * @param {string} pathname Current `location.pathname`
 * @param {string} nextPath Navigation target (`path` of the clicked item / sub-item).
 */
export default function dispatchPrivateNavPreNavigateResetActions(dispatch, pathname, nextPath) {
    if (!nextPath) {
        return;
    }
    const p = pathname || "";

    // MVP4: salir de `/transfer/summary` a dashboard/cuentas deja estado residual (summary, fetching, etc.).
    if (p === "/transfer/summary" && navigatesAwayFromTransferSummaryToOtherSection(nextPath)) {
        dispatch(transferActions.clearTransferData());
    }

    switch (nextPath) {
        case "/checksDetail":
            if (p !== "/checksDetail") {
                dispatch(checksActions.resetChecksFiltersForm());
            }
            break;
        case "/v2/echeqRules":
            if (p !== "/v2/echeqRules") {
                dispatch(globalActions.resetSelectedElements());
            }
            break;
        case "/v2/echeqs/mass-management":
            if (p !== "/v2/echeqs/mass-management") {
                dispatch(globalActions.resetSelectedElements());
            }
            break;
        case "/fundcorpOperations":
            prepareFundcorpOperationsEntry(dispatch, { optionSelected: "positions" });
            break;
        case "/cedip":
            if (p !== "/cedip") {
                dispatch(cedipActions.resetFilters());
                dispatch(cedipActions.setRecievedCedipSelected());
            }
            break;
        case "/financing/categories":
            if (p !== "/financing/categories") {
                dispatch(financingActions.resetDates());
            }
            break;
        case "/v2/agenda":
        case "/FrequentDestination":
            if (p !== "/v2/agenda") {
                dispatch(agendaActions.setIdContactDetail(""));
                dispatch(agendaActions.setSameHolder(false));
            }
            break;
        case "/v2/transfer/historic":
        case "/v3/transfer/historic":
            if (!isTransferHistoricPath(p)) {
                dispatch(transferActions.resetAllFilters());
                dispatch(transactionsActions.clearSelectedTransactionRows());
                dispatch(agendaActions.setSameHolderTransfer(false));
                dispatch(agendaActions.setSameHolderTransferForm(false));
            }
            break;
        case "/transfer/multiple":
            if (p !== "/transfer/multiple") {
                dispatch(globalActions.resetSelectedElements());
            }
            break;
        case "/payments/payDebin":
            if (p !== "/payments/payDebin") {
                dispatch(debinActions.resetFilters());
            }
            break;
        case "/payThirdParties/list":
            if (p !== "/payThirdParties/list") {
                dispatch(thirdPaymentsActions.resetFilters());
            }
            break;
        case "/servicePayments":
        case "/v2/payments-services": {
            const isInProgressFlow = isServicePaymentsModuleContinuationPath(p);
            if (!isInProgressFlow) {
                const entryPath = p && p !== "/servicePayments" && p !== "/v2/payments-services" ? p : null;
                dispatch(servicePaymentsActions.cleanUp());
                dispatch(paymentsAFIPActions.cleanUp());
                dispatch(servicePaymentsActions.setModuleEntryPath(entryPath));
            }
            break;
        }
        case "/ecomex/consultOperations":
            dispatch(globalActions.resetSelectedElements());
            break;
        case "/ecomex/informativeRegime":
            if (p !== "/ecomex/informativeRegime") {
                dispatch(globalActions.resetSelectedElements());
            }
            break;
        case "/ecomex/alertsAndDues":
            if (p !== "/ecomex/alertsAndDues") {
                dispatch(globalActions.resetSelectedElements());
            }
            break;
        case "/transactions/list":
            if (p !== "/transactions/list") {
                dispatch(globalActions.resetSelectedElements());
            }
            break;
        case "/charges/generateDebin":
            if (p !== "/charges/generateDebin") {
                dispatch(globalActions.resetSelectedElements());
            }
            break;
        case "/v2/directDebit/list":
            if (p !== "/v2/directDebit/list") {
                dispatch(globalActions.resetSelectedElements());
            }
            break;
        case "/pendingTransaction/list":
            if (p !== "/pendingTransaction/list") {
                dispatch(globalActions.resetSelectedElements());
                dispatch(transactionsActions.resetFilters());
            }
            break;
        default:
            break;
    }
}
